import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { questionHelpers, questionsActions } from '../questions';
import { appointmentActions, getClientDetails } from '../../api';
import { isEmpty } from '~/utils';
import { apiCallFinished } from '../loading-spinner';
import { YOUR_DETAILS_FIELDS_REVIEW } from '~/components/review-details/your-details/you-details-fields-review.config';
import { errorMessagesActions, errorTypes } from '~/components/messages';
import { envConfigSelectors } from '~/components/configuration/configuration.selectors';

export const withRequiredPrecondition = (Component) =>
  class ComponentWithRequiredPrecondition extends PureComponent {
    state = {
      isPreconditionCompleted: false,
    };

    static propTypes = {
      isServiceWithoutShops: PropTypes.bool,
      isServiceWithoutPartners: PropTypes.bool,

      draftShopId: PropTypes.string.isRequired,
      draftAppointmentTypeId: PropTypes.string.isRequired,
      draftPartnerId: PropTypes.string,
      draftTimeSlotFrom: PropTypes.number.isRequired,
      draftAddress: PropTypes.shape({}),
      draftAddressDescription: PropTypes.shape({}),
      phoneNumberRegex: PropTypes.shape({}).isRequired,

      history: PropTypes.shape({}).isRequired,
      addressQuestionId: PropTypes.string,
      hasAddressPage: PropTypes.bool.isRequired,
      firstPageNotRequiredToken: PropTypes.bool.isRequired,

      dispatchApiCallFinished: PropTypes.func.isRequired,
      dispatchResetErrorMessagesWith: PropTypes.func.isRequired,
      dispatchGetAppointment: PropTypes.func.isRequired,
    };

    static defaultProps = {
      isServiceWithoutShops: false,
      isServiceWithoutPartners: false,

      draftPartnerId: null,
      addressQuestionId: null,
      draftAddress: null,
      draftAddressDescription: null,
    };

    componentDidMount() {
      const {
        dispatchApiCallFinished,
        history: {
          location: { pathname },
        },
        dispatchResetErrorMessagesWith,
        firstPageNotRequiredToken,
        draftAppointmentTypeId,
      } = this.props;

      dispatchApiCallFinished(false);

      if (isEmpty(draftAppointmentTypeId) && !firstPageNotRequiredToken) {
        dispatchResetErrorMessagesWith([{ type: errorTypes.EMPTY_TOKEN_DATA_ERROR }]);
        dispatchApiCallFinished(true);
      } else if (this.isPageAfterAboutYou(pathname)) {
        this.checkPreconditionAfterAboutYou();
      } else {
        switch (pathname) {
          case '/about-you':
            this.checkAboutYouPrecondition();
            break;
          case '/select-date':
          case '/partners':
          case '/select-treatment':
            this.isShopSelected();
            break;
          default:
            this.handleSuccessPrecondition();
        }
      }
    }

    checkAboutYouPrecondition = () => {
      const { draftTimeSlotFrom } = this.props;

      if (draftTimeSlotFrom > 0) {
        this.handleSuccessPrecondition();
      } else {
        this.handleErrorPrecondition();
      }
    };

    isShopSelected = () => {
      const { draftShopId, isServiceWithoutShops } = this.props;
      if (isEmpty(draftShopId) && !isServiceWithoutShops) {
        this.handleErrorPrecondition();
      } else {
        this.handleSuccessPrecondition();
      }
    };

    handleSuccessPrecondition = () => {
      this.setState({ isPreconditionCompleted: true });
    };

    handleErrorPrecondition = (response = {}) => {
      const { dispatchResetErrorMessagesWith, dispatchApiCallFinished } = this.props;
      if (response.status !== 401) {
        dispatchResetErrorMessagesWith([{ type: errorTypes.TOKEN_INCONSISTENT_DATA_ERROR }]);
        dispatchApiCallFinished(true);
      }
    };

    isPageAfterAboutYou = (path) =>
      [
        '/review',
        '/edit-shop',
        '/edit-address',
        '/edit-date',
        '/edit-your-details',
        '/edit-question-answers',
      ].some((pathname) => path.indexOf(pathname) === 0);

    checkPreconditionAfterAboutYou = async () => {
      const { draftShopId, draftAppointmentTypeId, dispatchGetAppointment, isServiceWithoutShops } =
        this.props;

      const isShopIdNotValid = !isServiceWithoutShops && isEmpty(draftShopId);

      if (isShopIdNotValid || isEmpty(draftAppointmentTypeId)) {
        return this.handleErrorPrecondition();
      }

      return dispatchGetAppointment(draftShopId, [404])
        .then((appointment) => this.checkAppointmentState(appointment))
        .then(() => this.checkYourDetails())
        .then(this.handleSuccessPrecondition, this.handleErrorPrecondition);
    };

    checkAppointmentState = (appointment) => {
      const {
        draftPartnerId,
        draftTimeSlotFrom,
        draftAddress,
        draftAddressDescription,
        draftAppointmentTypeId,
        isServiceWithoutPartners,
      } = this.props;

      const { partner, time: timeSlotFrom, answers } = appointment;

      const address = draftAddressDescription.id
        ? draftAddressDescription.description
        : questionHelpers.formatAddressAnswerToString(draftAddress)[0];

      return (isServiceWithoutPartners ||
        this.isEqualDraftAndAppointmentPartner(draftPartnerId, partner.id)) &&
        this.isEqualDraftAndAppointmentTimeSlot(draftTimeSlotFrom, timeSlotFrom) &&
        this.isEqualDraftAndAppointmentAddress(address, answers)
        ? this.checkRequiredAnswers(draftAppointmentTypeId, draftAddress?.postcode, answers)
        : Promise.reject();
    };

    isEqualDraftAndAppointmentPartner = (draftPartnerId, partnerId) =>
      !draftPartnerId || partnerId === draftPartnerId;

    isEqualDraftAndAppointmentTimeSlot = (draftTimeSlotFrom, time) => time === draftTimeSlotFrom;

    isEqualDraftAndAppointmentAddress = (draftAddress, answers) => {
      const { addressQuestionId, hasAddressPage } = this.props;

      if (hasAddressPage) {
        const appointmentAddress = answers.find(
          (answer) => answer.questionId === addressQuestionId,
        );

        return !!appointmentAddress && appointmentAddress.answers[0] === draftAddress;
      }
      return true;
    };

    checkRequiredAnswers = (draftAppointmentTypeId, postcode, answers) => {
      const isAnswersEmpty = answers.length === 0;
      return questionsActions
        .getQuestions(draftAppointmentTypeId, postcode)
        .then(({ questions }) => {
          const hasRequiredQuestion = questions.some((question) => question.required);
          return isAnswersEmpty && hasRequiredQuestion ? Promise.reject() : Promise.resolve();
        });
    };

    checkYourDetails = () =>
      getClientDetails().then((clientDetails) => {
        const { firstName, lastName, phoneNumber } = clientDetails;
        if (!isEmpty(firstName) && !isEmpty(lastName) && this.isValidPhoneNumber(phoneNumber)) {
          return Promise.resolve();
        }
        return Promise.reject();
      });

    isValidPhoneNumber = (phoneNumber) => {
      const { phoneNumberRegex } = this.props;
      return !!phoneNumber.match(phoneNumberRegex);
    };

    render() {
      const { isPreconditionCompleted } = this.state;
      if (isPreconditionCompleted) {
        return <Component {...this.props} />;
      }
      return null;
    }
  };

const mapDispatchToProps = {
  dispatchApiCallFinished: apiCallFinished,
  dispatchResetErrorMessagesWith: errorMessagesActions.resetErrorMessagesWith,
  dispatchGetAppointment: appointmentActions.getAppointment,
};

function getPhoneNumberRegex(ReviewForm) {
  if (
    ReviewForm.yourDetailsFields &&
    ReviewForm.yourDetailsFields[2] &&
    ReviewForm.yourDetailsFields[2].fieldExtraProps.regex
  ) {
    return ReviewForm.yourDetailsFields[2].fieldExtraProps.regex;
  }
  return YOUR_DETAILS_FIELDS_REVIEW.phoneNumber.regex;
}

export const mapStateToProps = (state) => {
  const {
    appointment: {
      draft: {
        address: stateAddress,
        addressDescription: stateAddressDescription,
        shop: { id: stateShopId },
        partner: { id: statePartnerId },
        appointmentType: { id: draftAppointmentTypeId },
        timeSlot: { from: stateTimeSlotFrom },
      },
    },
    editAppointment: { temporaryAppointment },
    envConfig,
    services: {
      config: {
        BookingReview = {},
        ReviewForm = {},
        firstPageNotRequiredToken = false,
        isServiceWithoutShops,
        isServiceWithoutPartners,
      },
    },
  } = state;
  return {
    isServiceWithoutShops,
    isServiceWithoutPartners,
    draftShopId: !isEmpty(temporaryAppointment) ? temporaryAppointment.shop.id : stateShopId,
    draftPartnerId: !isEmpty(temporaryAppointment)
      ? temporaryAppointment.partner.id
      : statePartnerId,
    draftAppointmentTypeId,
    draftTimeSlotFrom: !isEmpty(temporaryAppointment)
      ? temporaryAppointment.timeSlot.from
      : stateTimeSlotFrom,
    draftAddress: !isEmpty(temporaryAppointment) ? temporaryAppointment.address : stateAddress,
    draftAddressDescription: !isEmpty(temporaryAppointment)
      ? temporaryAppointment.addressDescription
      : stateAddressDescription,
    addressQuestionId: envConfigSelectors.selectAppointmentTypeConfig(
      envConfig,
      draftAppointmentTypeId,
    ).addressQuestionId,
    hasAddressPage: !!BookingReview && !!BookingReview.displayUserAddress,
    phoneNumberRegex: getPhoneNumberRegex(ReviewForm),
    firstPageNotRequiredToken,
  };
};

export const ConnectedWithRequiredPrecondition = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(withRequiredPrecondition(Component));
